const COOKIE_EXPIRY_HOURS = 24;
const ACCORDION_STATE_PREFIX = 'accordion_state_';
const SERIES_ACCORDION_STATE_PREFIX = `${ACCORDION_STATE_PREFIX}series_`;
const HIDE_SORT_MODEL_CONFIRM_STATE_PREFIX = `hide_sort_model_confirm_state`;

export const saveAccordionState = (
  tenantGuid: string,
  isExpanded: boolean,
  categoryGuid: string | null,
  groupGuid: string | null,
): void => {
  if (!tenantGuid) return;

  const cookieName = `${ACCORDION_STATE_PREFIX}${categoryGuid ?? groupGuid}-${tenantGuid}`;
  const expiryDate = new Date();
  expiryDate.setHours(expiryDate.getHours() + COOKIE_EXPIRY_HOURS);

  document.cookie = `${cookieName}=${encodeURIComponent(JSON.stringify(isExpanded))}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
};

/**
 * Load accordion state from cookie with validation
 */
export const getAccordionState = (
  tenantGuid: string,
  categoryGuid: string | null,
  groupGuid: string | null
): boolean => {
  if (!tenantGuid) {
    return false;
  }

  const cookieName = `${ACCORDION_STATE_PREFIX}${categoryGuid ?? groupGuid}-${tenantGuid}`;
  const cookieValue = getCookie(cookieName);

  if (!cookieValue) {
    return false;
  }

  try {
    const result: boolean = JSON.parse(decodeURIComponent(cookieValue));
    return result ?? false;
  } catch (error) {
    // Invalid cookie format, delete it and return empty state
    deleteCookie(cookieName);
    return false;
  }
};


/**
 * Load series accordion state from cookie with validation
 */
export const getSeriesAccordionState = (
  tenantGuid: string,
  seriesGuid: string
): boolean => {
  if (!tenantGuid) {
    return false;
  }

  const cookieName = `${SERIES_ACCORDION_STATE_PREFIX}${seriesGuid}-${tenantGuid}`;
  const cookieValue = getCookie(cookieName);

  if (!cookieValue) {
    return false;
  }

  try {
    const result: boolean = JSON.parse(decodeURIComponent(cookieValue));
    return result ?? false;
  } catch (error) {
    // Invalid cookie format, delete it and return empty state
    deleteCookie(cookieName);
    return false;
  }
};

export const saveSeriesAccordionState = (
  tenantGuid: string,
  isExpanded: boolean,
  seriesGuid: string
): void => {
  if (!tenantGuid) return;

  const cookieName = `${SERIES_ACCORDION_STATE_PREFIX}${seriesGuid}-${tenantGuid}`;
  const expiryDate = new Date();
  expiryDate.setHours(expiryDate.getHours() + COOKIE_EXPIRY_HOURS);

  document.cookie = `${cookieName}=${encodeURIComponent(JSON.stringify(isExpanded))}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
};

/**
 * Load the hide sort model confirm state from cookie with validation
 */
export const getHideSortModelConfirmState = (): boolean => {
  const cookieName = HIDE_SORT_MODEL_CONFIRM_STATE_PREFIX;
  const cookieValue = getCookie(cookieName);

  if (!cookieValue) {
    return false;
  }

  try {
    const result: boolean = JSON.parse(decodeURIComponent(cookieValue));
    return result ?? false;
  } catch (error) {
    // Invalid cookie format, delete it and return empty state
    deleteCookie(cookieName);
    return false;
  }
};

export const saveHideSortModelConfirmState = (hideModal: boolean): void => {
  const cookieName = HIDE_SORT_MODEL_CONFIRM_STATE_PREFIX;
  const expiryDate = new Date();
  expiryDate.setHours(expiryDate.getHours() + COOKIE_EXPIRY_HOURS);

  document.cookie = `${cookieName}=${encodeURIComponent(
    JSON.stringify(hideModal)
  )}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
};

export const removeHideSortModelConfirmState = (): void => {
  deleteCookie(HIDE_SORT_MODEL_CONFIRM_STATE_PREFIX);
};

/**
 * Get a cookie value by name
 */
const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    let c = cookie.trim();
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length);
    }
  }

  return null;
};

/**
 * Delete a cookie by name
 */
const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};